import * as React from 'react';
import { useObjectRef } from '@react-aria/utils';
import { VariantProps, cva, cx } from 'class-variance-authority';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { Outline } from '../outline';

type ButtonProps = Omit<React.ComponentProps<'button'>, 'ref'> &
  VariantProps<typeof compClasses> & {
    asChild?: boolean;
    className?: string;
    loadingText?: string;
    children: React.ReactNode;
    outline?:
      | 'default'
      | 'primary'
      | 'secondary'
      | 'tertiary'
      | null
      | undefined;
  };

export const ButtonV2 = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (props, forwardedRef): JSX.Element => {
    const {
      asChild,
      children,
      variant,
      variantStyle,
      size,
      className,
      disabled = false,
      loading,
      loadingText,
      outline,
      ...rest
    } = props;

    const ComponentWrap = (asChild ? Slot : 'button') as 'button';
    const ref = useObjectRef(forwardedRef);

    if (outline) {
      return (
        <Outline variantStyle={outline} className='rounded-full'>
          <ComponentWrap
            ref={ref}
            className={cx(
              compClasses({ variant, variantStyle, size, disabled, loading }),
              className
            )}
            disabled={Boolean(loading || disabled)}
            {...rest}
          >
            {loading ? (
              loadingText ? (
                loadingText
              ) : (
                'loading'
              )
            ) : (
              <Slottable> {children}</Slottable>
            )}
          </ComponentWrap>
        </Outline>
      );
    }
    return (
      <ComponentWrap
        ref={ref}
        className={cx(
          compClasses({ variant, variantStyle, size, disabled, loading }),
          className
        )}
        disabled={Boolean(loading || disabled)}
        {...rest}
      >
        {loading ? (
          loadingText ? (
            loadingText
          ) : (
            'loading'
          )
        ) : (
          <Slottable> {children}</Slottable>
        )}
      </ComponentWrap>
    );
  }
);

const compClasses = cva(
  [
    'inline-flex',
    'rounded-lg',
    'whitespace-nowrap',
    'items-center',
    'justify-center',
  ],
  {
    defaultVariants: {
      size: 'medium',
    },
    variants: {
      variant: {
        default: '',
        pills: 'px-3 text-base py-2',
        ghost: '',
        outline: '',
      },
      variantStyle: {
        primary: '',
        secondary: '',
        tertiary: '',
        quaternary: '',
        quinary: '',
        accent: '',
        warning: '',
        danger: '',
        ghost: '',
      },
      disabled: {
        true: 'cursor-not-allowed',
      },
      loading: {
        true: 'cursor-progress',
      },
      size: {
        none: '',
        small: 'text-xs font-medium px-3 py-2',
        medium: 'text-sm font-medium px-4 py-2 ',
        large: 'text-md font-medium px-4 py-2',
        xlarge: 'text-lg font-medium px-6 py-4',
      },
    },
    compoundVariants: [
      {
        variant: 'default',
        variantStyle: 'ghost',
        disabled: false,
        className:
          'text-black font-medium hover:underline border border-grey-150',
      },

      {
        variant: 'default',
        variantStyle: 'danger',
        disabled: false,
        className:
          'bg-amber-700 text-white font-medium border-amber-700 hover:shadow-error hover:border-amber-700 border',
      },
      {
        variant: 'default',
        variantStyle: 'danger',
        disabled: true,
        className:
          'bg-amber-700/75 text-white/75 font-medium border-amber-700  border',
      },
      {
        variant: 'default',
        variantStyle: 'primary',
        disabled: false,
        className:
          'bg-green-25 text-blue-1000 font-medium border-green-25 hover:shadow-default hover:border-blue-600 border',
      },
      {
        variant: 'default',
        variantStyle: 'quaternary',
        disabled: true,
        className: 'bg-blue-500/75 text-white border-green-25/75 font-medium',
      },
      {
        variant: 'default',
        variantStyle: 'quaternary',
        disabled: false,
        className:
          'bg-blue-500/100 text-white font-medium hover:border-blue-500',
      },
      {
        variant: 'default',
        variantStyle: 'quinary',
        disabled: true,
        className:
          'bg-blue-500/20 text-blue-500 border-blue-500/20 font-medium',
      },
      {
        variant: 'default',
        variantStyle: 'quinary',
        disabled: false,
        className:
          'bg-blue-500/20 text-blue-500 font-medium border-blue-500/20 hover:shadow-blue500/20 hover:border-blue-500/20 border',
      },
      {
        variant: 'default',
        variantStyle: 'primary',
        disabled: true,
        className:
          'bg-green-25/75 text-blue-1000  border-green-25/75 font-medium',
      },
      {
        variant: 'default',
        variantStyle: 'secondary',
        disabled: false,
        className:
          'bg-purple-500 text-white font-medium border-purple-500 hover:shadow-purple-500 hover:border-purple-500 border',
      },
      {
        variant: 'default',
        variantStyle: 'secondary',
        disabled: true,
        className:
          'bg-purple-500/75 text-white/75 border-purple-500/75 font-medium',
      },
      {
        variant: 'default',
        variantStyle: 'warning',
        disabled: false,
        className:
          'bg-yellow text-white font-medium border-yellow hover:shadow-warning border',
      },
      {
        variant: 'default',
        variantStyle: 'warning',
        disabled: true,
        className: 'bg-yellow/75 text-white/75 border-yellow/75 font-medium',
      },
      {
        variant: 'default',
        variantStyle: 'tertiary',
        disabled: false,
        className:
          'bg-blue-300/25 text-white  border-blue-300 font-medium  hover:shadow-blue hover:bg-blue-300',
      },
      {
        variant: 'default',
        variantStyle: 'tertiary',
        disabled: true,
        className: 'bg-blue-300/75 text-white/75 border-blue-300 font-medium',
      },
      {
        variant: 'default',
        variantStyle: 'accent',
        disabled: false,
        className:
          'bg-blue-1000 text-white  border-blue-300 font-medium  hover:shadow-purple-500 hover:border-purple-500',
      },
      {
        variant: 'default',
        variantStyle: 'accent',
        disabled: true,
        className: 'bg-blue-600/75 text-white/75 border-blue-300 font-medium',
      },
      {
        variant: 'default',
        variantStyle: 'warning',
        disabled: false,
        className:
          'bg-blue-1000 text-white  border-blue-300 font-medium  hover:shadow-purple-500 hover:border-purple-500',
      },
      {
        variant: 'default',
        variantStyle: 'warning',
        disabled: true,
        className: 'bg-blue-600/50 text-white  border-blue-300 font-medium',
      },
      {
        variant: 'default',
        variantStyle: 'warning',
        disabled: false,
        className:
          'bg-blue-1000 text-white  border-blue-300 font-medium  hover:shadow-purple-500 hover:border-purple-500',
      },
      {
        variant: 'default',
        variantStyle: 'warning',
        disabled: true,
        className: 'bg-blue-600/50 text-white  border-blue-300 font-medium',
      },
      {
        variant: 'pills',
        variantStyle: 'primary',
        disabled: false,
        className:
          'text-black bg-green-25 font-bold rounded-full border-green-25 border hover:shadow-default hover:text-black hover:bg-green-25 uppercase',
      },
      {
        variant: 'pills',
        variantStyle: 'primary',
        disabled: true,
        className:
          'text-black bg-green-25/75 font-bold rounded-full border-green-25  hover:text-blue-1000',
      },
      {
        variant: 'pills',
        variantStyle: 'secondary',
        disabled: false,
        className:
          'text-white font-bold rounded-full border-green-25 border hover:shadow-default hover:text-blue-1000 hover:border-black-100 hover:bg-green-25 uppercase',
      },
      {
        variant: 'pills',
        variantStyle: 'warning',
        disabled: false,
        className:
          'text-white font-bold rounded-full border-yellow border hover:shadow-warning',
      },
      {
        variant: 'ghost',
        variantStyle: 'primary',
        disabled: false,
        className: 'text-black',
      },
      {
        variant: 'ghost',
        variantStyle: 'secondary',
        disabled: false,
        className: 'text-white font-medium hover:underline ',
      },
      {
        variant: 'ghost',
        variantStyle: 'quaternary',
        disabled: false,
        className: 'text-blue-500 font-medium hover:underline ',
      },
      {
        variant: 'outline',
        variantStyle: 'primary',
        disabled: false,
        className:
          'border-green-25 text-black font-medium hover:shadow-default hover:border-green-25 border',
      },
      {
        variant: 'outline',
        variantStyle: 'primary',
        disabled: true,
        className: 'border-green-25 text--blue-500 font-medium  border',
      },
      {
        variant: 'outline',
        variantStyle: 'quaternary',
        disabled: true,
        className: 'border border-blue-500/75 text-blue-500/75 font-medium',
      },
      {
        variant: 'outline',
        variantStyle: 'quaternary',
        disabled: false,
        className:
          'border-blue-500 text-blue-500 font-medium hover:shadow-blue500 hover:border-blue-500 border',
      },
    ],
  }
);
